import React, { useState } from 'react';
import { Button, Form, Layout, Select } from 'antd';
import { MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import FilterForm, { getFilter } from './FilterForm';
import EditGlobalFilterDrawer from './EditGlobalFilterDrawer';

import './global-filter.less';

const { Sider } = Layout;

const CREATE_GLOBAL_FILTER_MUTATION = gql`
  mutation CreateGlobalFilter($customerId: ID!, $input: GlobalFilterInput!) {
    createGlobalFilter(customerId: $customerId, input: $input) {
      id
    }
  }
`;

const UPDATE_GLOBAL_FILTER_MUTATION = gql`
  mutation UpdateGlobalFilter($id: ID!, $input: GlobalFilterInput!) {
    updateGlobalFilter(id: $id, input: $input) {
      id
    }
  }
`;

const DELETE_GLOBAL_FILTER_MUTATION = gql`
  mutation DeleteGlobalFilter($id: ID!) {
    deleteGlobalFilter(id: $id) {
      id
    }
  }
`;

const GlobalFilter = ({
  title,
  onChange,
  onSelectedFilterChange,
  onRemove,
  collapsed,
  onCollapse,
  filter,
  globalFilters,
  websites,
  customerId,
}) => {
  const [editGlobalFilter, setEditGlobalFilter] = useState(false);
  const [createGlobalFilter] = useMutation(CREATE_GLOBAL_FILTER_MUTATION);
  const [updateGlobalFilter] = useMutation(UPDATE_GLOBAL_FILTER_MUTATION);
  const [deleteGlobalFilter] = useMutation(DELETE_GLOBAL_FILTER_MUTATION);

  const onSaveFilter = async values => {
    if (!filter.id) {
      const { data } = await createGlobalFilter({
        variables: {
          customerId,
          input: values,
        },
      });
      await onChange?.();
      onSelectedFilterChange?.(data.createGlobalFilter.id);
      return;
    }

    await updateGlobalFilter({
      variables: {
        id: filter.id,
        input: values,
      },
    });
    onChange?.();
  };

  const onRemoveFilter = async () => {
    await deleteGlobalFilter({ variables: { id: filter.id } });
    onRemove?.();
  };

  const onCreateFilter = async () => {
    const { data } = await createGlobalFilter({
      variables: {
        customerId,
        input: getFilter({ title: 'New filter' }),
      },
    });
    await onChange?.();
    onSelectedFilterChange?.(data.createGlobalFilter.id);
    setEditGlobalFilter(false);
  };

  const onDuplicateFilter = async () => {
    const input = getFilter(filter);
    input.title = filter.title ? `${filter.title} (copy)` : 'Unnamed filter (copy)';
    input.segments = filter.segments?.map(({ id: _id, __typename, dateRangeConfig, ...segment }) => ({
      ...segment,
      dateRangeConfig: dateRangeConfig ? { ...dateRangeConfig, id: undefined } : undefined,
    }));

    const { data } = await createGlobalFilter({
      variables: {
        customerId,
        input,
      },
    });
    await onChange?.();
    onSelectedFilterChange?.(data.createGlobalFilter.id);
    setEditGlobalFilter(false);
  };

  const filterOptions = globalFilters?.map(f => ({ label: f.title || 'Unnamed filter', value: f.id })) || [];

  return (
    <Sider className="global-filter" width={300} theme="light" collapsedWidth={42} collapsed={collapsed}>
      {!collapsed && (
        <>
          <div className="global-filter__header">
            {title}
            <Button
              aria-label="Close filter panel"
              onClick={() => onCollapse(true)}
              icon={<MenuFoldOutlined />}
              type="link"
            />
          </div>
          <div className="global-filter__body">
            {globalFilters?.length > 0 && (
              <Form layout="vertical">
                <Form.Item
                  label="Saved filter"
                  extra={(
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <a role="button" tabIndex={0} onClick={() => setEditGlobalFilter(true)}><small>Manage saved filters</small></a>
                    </div>
                  )}
                  style={{ marginBottom: 0 }}
                >
                  <Select
                    value={filter?.id}
                    onChange={onSelectedFilterChange}
                    options={filterOptions}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Form>
            )}
            <FilterForm
              onChange={onSaveFilter}
              filter={filter}
              websites={websites}
            />
          </div>
        </>
      )}
      {
        collapsed && (
          <div className="global-filter__header-collapsed">
            <Button
              aria-label="Open filter panel"
              onClick={() => onCollapse(false)}
              icon={<MenuUnfoldOutlined />}
              type="link"
            />
          </div>
        )
      }
      <EditGlobalFilterDrawer
        open={editGlobalFilter}
        globalFilter={filter}
        onSubmit={onSaveFilter}
        onRemove={onRemoveFilter}
        onCreate={onCreateFilter}
        onDuplicate={onDuplicateFilter}
        onCancel={() => setEditGlobalFilter(false)}
      />
    </Sider>
  );
};

export default GlobalFilter;
